import { toCanvas } from 'html-to-image';

/** `data-feedback-panel` on the panel root keeps it out of its own shots. */
export const FEEDBACK_PANEL_ATTRIBUTE = 'feedbackPanel';

export type ScreenshotResult =
  { ok: true; blob: Blob } | { ok: false; error: string };

/**
 * A JPEG of the visible viewport, with any open dialog left out.
 *
 * Captures only the viewport (not the full scrollable body) and shifts the
 * clone by the current scroll offset so the image matches what the user sees.
 * Encoding goes through `canvas.toBlob`, which browsers run off the main
 * thread, so the dialog's textarea stays responsive while the shot finalizes.
 */
export async function captureViewportScreenshot(): Promise<ScreenshotResult> {
  try {
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    const canvas = await toCanvas(document.body, {
      width: window.innerWidth,
      height: window.innerHeight,
      // Reference shots, not retina assets: the default devicePixelRatio
      // (2–3x) inflates the canvas 4–9x and is the biggest cause of
      // main-thread blocking during capture.
      pixelRatio: Math.min(window.devicePixelRatio, 1.5),
      style: {
        transform: `translate(${-scrollX}px, ${-scrollY}px)`,
        transformOrigin: 'top left',
      },
      // Skip font embedding — avoids CORS errors from Google Fonts / external
      // stylesheets whose cssRules are unreadable cross-origin in production.
      skipFonts: true,
      // No `?cacheBust=…` on <img> URLs; it breaks signed images and CDNs
      // that vary on the exact URL.
      cacheBust: false,
      imagePlaceholder:
        'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
      onImageErrorHandler: () => {
        // A broken icon shouldn't kill the screenshot.
      },
      filter: shouldCaptureNode,
    });

    const blob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) =>
          b
            ? resolve(b)
            : reject(new Error('Canvas encoding produced no blob')),
        'image/jpeg',
        0.6,
      );
    });
    return { ok: true, blob };
  } catch (err) {
    console.error('[Feedback] screenshot capture failed:', err);
    return { ok: false, error: describeCaptureError(err) };
  }
}

/**
 * Which nodes go into the shot. Excluding an ancestor drops its whole subtree.
 */
export function shouldCaptureNode(node: Node): boolean {
  // <noscript> fallback text ("You need to enable JavaScript…") would render in
  // the cloned SVG, where scripting is off.
  if (node.nodeName === 'SCRIPT' || node.nodeName === 'NOSCRIPT') return false;
  // Leave out the feedback panel itself and any open dialog (every dialog
  // slot carries a `data-slot` starting with `dialog-`).
  if (node instanceof HTMLElement) {
    if (FEEDBACK_PANEL_ATTRIBUTE in node.dataset) return false;
    if (node.dataset.slot?.startsWith('dialog-')) return false;
  }
  return true;
}

export function describeCaptureError(err: unknown): string {
  if (err instanceof Error) return err.message;
  // html-to-image rejects with the raw `error` Event for image load failures;
  // `String(event)` is "[object Event]", which is useless to show the user.
  if (err instanceof Event) {
    const target = err.target;
    if (target instanceof HTMLImageElement && target.src) {
      return `Failed to load image: ${target.src}`;
    }
    return `Failed to load a page resource (${err.type})`;
  }
  if (typeof err === 'string') return err;
  return 'Unknown error while capturing the screenshot';
}
